import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, Wallet, DollarSign } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useCurrency } from '@/hooks/useCurrency';
import { useCustomerAccounts, type CustomerAccount } from '@/hooks/useCustomerAccounts';

const formSchema = z.object({
  amount: z.coerce.number().positive('Amount must be greater than 0'),
  notes: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  account: CustomerAccount | null;
}

const QUICK_AMOUNTS = [10, 20, 50, 100];

export function WalletTopUpDialog({ open, onOpenChange, account }: Props) {
  const { formatCurrency } = useCurrency();
  const { topUpWallet } = useCustomerAccounts();
  const [selectedQuickAmount, setSelectedQuickAmount] = useState<number | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      amount: 0,
      notes: '',
    },
  });

  const amount = watch('amount');

  const onSubmit = async (data: FormValues) => {
    if (!account) return;

    try {
      const result = await topUpWallet.mutateAsync({
        accountId: account.id,
        amount: data.amount,
        notes: data.notes || undefined,
      });

      toast.success('Wallet Topped Up', {
        description: `Added ${formatCurrency(data.amount)} to wallet. New balance: ${formatCurrency(result.newBalance)}`,
      });

      reset();
      setSelectedQuickAmount(null);
      onOpenChange(false);
    } catch (error: any) {
      toast.error('Error', {
        description: error?.message || 'Failed to top up wallet',
      });
    }
  };

  const handleQuickAmount = (value: number) => {
    setSelectedQuickAmount(value);
    setValue('amount', value);
  };

  if (!account) return null;

  const customerName = `${account.first_name || ''} ${account.last_name || ''}`.trim() || account.username;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            Top Up Wallet
          </DialogTitle>
          <DialogDescription>
            Add funds to {customerName}&apos;s wallet
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {/* Current Balance */}
          <div className="bg-muted/50 rounded-lg p-4 mb-4">
            <p className="text-sm text-muted-foreground">Current Balance</p>
            <p className="text-2xl font-bold">{formatCurrency(account.wallet_balance)}</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Quick Amount Buttons */}
            <div className="space-y-2">
              <Label>Quick Amount</Label>
              <div className="grid grid-cols-4 gap-2">
                {QUICK_AMOUNTS.map((value) => (
                  <Button
                    key={value}
                    type="button"
                    variant={selectedQuickAmount === value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleQuickAmount(value)}
                  >
                    {formatCurrency(value)}
                  </Button>
                ))}
              </div>
            </div>

            {/* Custom Amount */}
            <div className="space-y-2">
              <Label htmlFor="amount">Amount *</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  {...register('amount')}
                  className="pl-9"
                  placeholder="0.00"
                  onChange={(e) => {
                    setSelectedQuickAmount(null);
                    register('amount').onChange(e);
                  }}
                />
              </div>
              {errors.amount && (
                <p className="text-xs text-destructive">{errors.amount.message}</p>
              )}
            </div>

            {/* New Balance Preview */}
            {amount > 0 && (
              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                <p className="text-sm text-muted-foreground">New Balance After Top-Up</p>
                <p className="text-lg font-semibold text-green-600">
                  {formatCurrency(account.wallet_balance + Number(amount))}
                </p>
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                {...register('notes')}
                placeholder="e.g., Cash payment received"
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" variant="blaze" disabled={isSubmitting || amount <= 0}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Top Up {amount > 0 && formatCurrency(amount)}
              </Button>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}


















